/**
 * IntakeAssessmentPayloadForm.tsx — Issue #343.
 *
 * Discriminated structured form for the three intake assessment record
 * kinds. Renders normal business fields (text, number, date, lists)
 * instead of a raw JSON textarea. Used by both the create page and the
 * detail edit panel.
 *
 * The component is controlled via a discriminated props union — the
 * `kind` literal disambiguates `values` / `onChange` so callers never
 * need a type cast. Serialization back to the backend `payload` string
 * is handled by `serializePayload()` from
 * `./intakeAssessmentPayloadForm.ts`, not by this component.
 */
import { type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  joinMultiLineList,
  splitMultiLineList,
  type DealIntentionValues,
  type GoldenAnalysisValues,
  type HealthAssessmentValues,
  type IntakeAssessmentPayloadFormState,
  type PayloadValidationError,
} from './intakeAssessmentPayloadFormModel';

interface CommonProps {
  disabled?: boolean;
  /** Field-level validation errors keyed by field name. */
  errors?: ReadonlyArray<PayloadValidationError>;
  /** Prefix for input `id`s so labels can target them; default "ia-payload". */
  idPrefix?: string;
}

export type IntakeAssessmentPayloadFormProps =
  | (CommonProps & {
      kind: 'golden_analysis';
      values: GoldenAnalysisValues;
      onChange: (next: GoldenAnalysisValues) => void;
    })
  | (CommonProps & {
      kind: 'deal_intention';
      values: DealIntentionValues;
      onChange: (next: DealIntentionValues) => void;
    })
  | (CommonProps & {
      kind: 'health_assessment';
      values: HealthAssessmentValues;
      onChange: (next: HealthAssessmentValues) => void;
    });

export function IntakeAssessmentPayloadForm(
  props: IntakeAssessmentPayloadFormProps,
): ReactNode {
  const prefix = props.idPrefix ?? 'ia-payload';
  const errorMap = new Map<string, string>();
  for (const e of props.errors ?? []) errorMap.set(e.field, e.message);

  switch (props.kind) {
    case 'golden_analysis':
      return (
        <GoldenAnalysisFields
          values={props.values}
          onChange={props.onChange}
          disabled={props.disabled}
          idPrefix={prefix}
        />
      );
    case 'deal_intention':
      return (
        <DealIntentionFields
          values={props.values}
          onChange={props.onChange}
          disabled={props.disabled}
          idPrefix={prefix}
          errors={errorMap}
        />
      );
    case 'health_assessment':
      return (
        <HealthAssessmentFields
          values={props.values}
          onChange={props.onChange}
          disabled={props.disabled}
          idPrefix={prefix}
        />
      );
    default: {
      const exhaustive: never = props;
      throw new Error(
        `Unknown intake assessment record kind: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}

/**
 * Narrows a discriminated `IntakeAssessmentPayloadFormState` into the
 * matching `IntakeAssessmentPayloadForm` instance so parent pages do
 * not have to do the switch (or use unsafe casts) themselves.
 */
export interface IntakeAssessmentPayloadFormSlotProps {
  state: IntakeAssessmentPayloadFormState;
  onChange: (next: IntakeAssessmentPayloadFormState) => void;
  disabled?: boolean;
  errors?: ReadonlyArray<PayloadValidationError>;
  idPrefix?: string;
}

export function IntakeAssessmentPayloadFormSlot(
  props: IntakeAssessmentPayloadFormSlotProps,
): ReactNode {
  const { state, onChange, disabled, errors, idPrefix } = props;
  switch (state.kind) {
    case 'golden_analysis':
      return (
        <IntakeAssessmentPayloadForm
          kind="golden_analysis"
          values={state.values}
          onChange={(values) => onChange({ kind: 'golden_analysis', values })}
          disabled={disabled}
          errors={errors}
          idPrefix={idPrefix}
        />
      );
    case 'deal_intention':
      return (
        <IntakeAssessmentPayloadForm
          kind="deal_intention"
          values={state.values}
          onChange={(values) => onChange({ kind: 'deal_intention', values })}
          disabled={disabled}
          errors={errors}
          idPrefix={idPrefix}
        />
      );
    case 'health_assessment':
      return (
        <IntakeAssessmentPayloadForm
          kind="health_assessment"
          values={state.values}
          onChange={(values) => onChange({ kind: 'health_assessment', values })}
          disabled={disabled}
          errors={errors}
          idPrefix={idPrefix}
        />
      );
    default: {
      const exhaustive: never = state;
      throw new Error(
        `Unknown intake assessment record kind: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}

// ============================================================================
// Subforms
// ============================================================================

interface SubformProps<V> {
  values: V;
  onChange: (next: V) => void;
  disabled?: boolean;
  idPrefix: string;
}

function GoldenAnalysisFields(
  props: SubformProps<GoldenAnalysisValues>,
): ReactNode {
  const { values, onChange, disabled, idPrefix } = props;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TextAreaField
        id={`${idPrefix}-painPoints`}
        label="痛点"
        value={values.painPoints}
        onChange={(v) => onChange({ ...values, painPoints: v })}
        disabled={disabled}
        placeholder="客户当前最关心的问题、症状或顾虑。"
        rows={3}
        spanFull
      />
      <TextField
        id={`${idPrefix}-budgetEstimate`}
        label="预算预估"
        value={values.budgetEstimate}
        onChange={(v) => onChange({ ...values, budgetEstimate: v })}
        disabled={disabled}
        placeholder="例如：12 万元 / 待沟通"
      />
      <TextField
        id={`${idPrefix}-decisionMakers`}
        label="决策人"
        value={values.decisionMakers}
        onChange={(v) => onChange({ ...values, decisionMakers: v })}
        disabled={disabled}
        placeholder="本人 / 配偶 / 子女 / 其他关键决策人"
      />
      <TextAreaField
        id={`${idPrefix}-concerns`}
        label="顾虑"
        value={values.concerns}
        onChange={(v) => onChange({ ...values, concerns: v })}
        disabled={disabled}
        placeholder="对价格、安全、效果、流程等的顾虑。"
        rows={3}
      />
      <TextAreaField
        id={`${idPrefix}-trustAnchors`}
        label="信任点"
        value={values.trustAnchors}
        onChange={(v) => onChange({ ...values, trustAnchors: v })}
        disabled={disabled}
        placeholder="客户认可的医生、案例、资质、推荐人等。"
        rows={3}
      />
      <TextAreaField
        id={`${idPrefix}-nextStep`}
        label="下一步"
        value={values.nextStep}
        onChange={(v) => onChange({ ...values, nextStep: v })}
        disabled={disabled}
        placeholder="下一步动作与时间承诺。"
        rows={2}
        spanFull
      />
    </div>
  );
}

function DealIntentionFields(
  props: SubformProps<DealIntentionValues> & {
    errors: Map<string, string>;
  },
): ReactNode {
  const { values, onChange, disabled, idPrefix, errors } = props;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TextField
        id={`${idPrefix}-intentLevel`}
        label="意向等级"
        value={values.intentLevel}
        onChange={(v) => onChange({ ...values, intentLevel: v })}
        disabled={disabled}
        placeholder="例如：A / B / C"
      />
      <TextField
        id={`${idPrefix}-probability`}
        label="成交概率（0 到 1 之间的小数字符串）"
        value={values.probability}
        onChange={(v) => onChange({ ...values, probability: v })}
        disabled={disabled}
        placeholder="例如：0.75"
        inputMode="decimal"
        error={errors.get('probability')}
      />
      <DateField
        id={`${idPrefix}-expectedClosingDate`}
        label="预计成交日期"
        value={values.expectedClosingDate}
        onChange={(v) => onChange({ ...values, expectedClosingDate: v })}
        disabled={disabled}
        error={errors.get('expectedClosingDate')}
      />
      <MultiLineListField
        id={`${idPrefix}-preferredProjects`}
        label="偏好项目（每行一个或用逗号分隔）"
        values={values.preferredProjects}
        onChange={(v) => onChange({ ...values, preferredProjects: v })}
        disabled={disabled}
        placeholder={'例如：免疫细胞治疗\n干细胞抗衰'}
        rows={3}
      />
      <TextAreaField
        id={`${idPrefix}-notes`}
        label="备注"
        value={values.notes}
        onChange={(v) => onChange({ ...values, notes: v })}
        disabled={disabled}
        placeholder="与成交相关的补充说明。"
        rows={3}
        spanFull
      />
    </div>
  );
}

function HealthAssessmentFields(
  props: SubformProps<HealthAssessmentValues>,
): ReactNode {
  const { values, onChange, disabled, idPrefix } = props;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TextAreaField
        id={`${idPrefix}-medicalHistorySummary`}
        label="病史摘要"
        value={values.medicalHistorySummary}
        onChange={(v) => onChange({ ...values, medicalHistorySummary: v })}
        disabled={disabled}
        placeholder="既往病史、手术史、家族史等关键摘要。"
        rows={3}
        spanFull
      />
      <TextAreaField
        id={`${idPrefix}-physicalExamFindings`}
        label="体检发现"
        value={values.physicalExamFindings}
        onChange={(v) => onChange({ ...values, physicalExamFindings: v })}
        disabled={disabled}
        placeholder="望闻问切及体检异常发现。"
        rows={3}
        spanFull
      />
      <MultiLineListField
        id={`${idPrefix}-labResults`}
        label="化验结果（每行一项或用逗号分隔）"
        values={values.labResults}
        onChange={(v) => onChange({ ...values, labResults: v })}
        disabled={disabled}
        placeholder={'例如：血常规-正常\n肝功能-ALT 略高'}
        rows={4}
        spanFull
      />
      <MultiLineListField
        id={`${idPrefix}-imagingResults`}
        label="影像结果（每行一项或用逗号分隔）"
        values={values.imagingResults}
        onChange={(v) => onChange({ ...values, imagingResults: v })}
        disabled={disabled}
        placeholder={'例如:胸部CT-未见明显异常\n腹部B超-肝囊肿'}
        rows={4}
        spanFull
      />
      <TextAreaField
        id={`${idPrefix}-immuneAssessment`}
        label="免疫评估"
        value={values.immuneAssessment}
        onChange={(v) => onChange({ ...values, immuneAssessment: v })}
        disabled={disabled}
        placeholder="免疫细胞数量、亚群、活性等评估结论。"
        rows={3}
        spanFull
      />
      <TextAreaField
        id={`${idPrefix}-doctorNotes`}
        label="医生备注"
        value={values.doctorNotes}
        onChange={(v) => onChange({ ...values, doctorNotes: v })}
        disabled={disabled}
        placeholder="医生侧补充判断与后续建议；不得直接面向客户作为诊断结论。"
        rows={3}
        spanFull
      />
    </div>
  );
}

// ============================================================================
// Field primitives
// ============================================================================

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: 'decimal' | 'numeric' | 'text';
  error?: string;
  spanFull?: boolean;
}

function TextField(props: TextFieldProps): ReactNode {
  const {
    id,
    label,
    value,
    onChange,
    disabled,
    placeholder,
    inputMode,
    error,
    spanFull,
  } = props;
  return (
    <div className={spanFull ? 'space-y-1 md:col-span-2' : 'space-y-1'}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
      />
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: string;
}

function DateField(props: DateFieldProps): ReactNode {
  const { id, label, value, onChange, disabled, error } = props;
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
      />
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  spanFull?: boolean;
}

function TextAreaField(props: TextAreaFieldProps): ReactNode {
  const {
    id,
    label,
    value,
    onChange,
    disabled,
    placeholder,
    rows,
    spanFull,
  } = props;
  return (
    <div className={spanFull ? 'space-y-1 md:col-span-2' : 'space-y-1'}>
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows ?? 3}
      />
    </div>
  );
}

interface MultiLineListFieldProps {
  id: string;
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  spanFull?: boolean;
}

function MultiLineListField(props: MultiLineListFieldProps): ReactNode {
  const {
    id,
    label,
    values,
    onChange,
    disabled,
    placeholder,
    rows,
    spanFull,
  } = props;
  return (
    <div className={spanFull ? 'space-y-1 md:col-span-2' : 'space-y-1'}>
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        value={joinMultiLineList(values)}
        onChange={(e) => onChange(splitMultiLineList(e.target.value))}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows ?? 3}
      />
    </div>
  );
}
